import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CreateAuditRecordDTO } from "@/modules/internal-audit/domain/contracts";
import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import {
  useAuditPlanDetailQuery,
  useAuditRecordQuery,
  useUpdateAuditRecord,
} from "../api/queries";
import { AuditRecordForm } from "../ui/AuditRecordForm";

export function AuditRecordEditPage() {
  const { id } = useParams({ from: "/app/internal-audit/records/$id/edit" });
  const nav = useNavigate();
  const recordQuery = useAuditRecordQuery(id);
  const updateMutation = useUpdateAuditRecord(id);

  // Get audit plan for the form
  const auditPlanQuery = useAuditPlanDetailQuery(
    recordQuery.data?.auditPlanId || "",
  );

  const handleBack = () => {
    nav({ to: "/app/internal-audit/records/$id", params: { id } });
  };

  const handleSubmit = async (data: {
    auditPlanId: string;
    auditDate: Date;
    auditorId: string;
    auditScope: string;
    overallResult: "pass" | "fail" | "needs_improvement";
    summary?: string | null;
    status: "draft" | "completed";
    items: Array<{
      title: string;
      description?: string | null;
      orderIndex: number;
      result: "pass" | "fail" | "needs_improvement";
      notes?: string | null;
      mediaAssetId?: string | null;
    }>;
    attachments: string[];
  }) => {
    // Prevent editing if already completed
    if (recordQuery.data?.status === "completed") {
      toast.error("ບໍ່ສາມາດແກ້ໄຂບັນທຶກທີ່ສຳເລັດແລ້ວໄດ້");
      return;
    }

    const updateData: CreateAuditRecordDTO = {
      auditPlanId: data.auditPlanId,
      auditDate: data.auditDate,
      auditorId: data.auditorId,
      auditScope: data.auditScope,
      overallResult: data.overallResult,
      summary: data.summary,
      status: data.status,
      items: data.items.map((item) => ({
        title: item.title,
        description: item.description,
        orderIndex: item.orderIndex,
        result: item.result,
        notes: item.notes,
      })),
      attachments: data.attachments,
    };

    try {
      await updateMutation.mutateAsync(updateData);
      nav({ to: "/app/internal-audit/records/$id", params: { id } });
    } catch (error) {
      // Error toast already handled by mutation
    }
  };

  if (recordQuery.isLoading || auditPlanQuery.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (recordQuery.error || !recordQuery.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            <p className="text-muted-foreground">ບໍ່ພົບບັນທຶກການກວດສອບ</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  if (recordQuery.data.status === "completed") {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            <p className="text-muted-foreground">ບໍ່ສາມາດແກ້ໄຂບັນທຶກທີ່ສຳເລັດແລ້ວໄດ້</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  if (!auditPlanQuery.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            <p className="text-muted-foreground">ບໍ່ພົບຜະແນການກວດສອບ</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handleBack}>
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-bold text-3xl">ແກ້ໄຂບັນທຶກການກວດສອບ</h1>
            <p className="mt-1 text-muted-foreground">
              ຜະແນການກວດສອບ: {auditPlanQuery.data.name}
            </p>
          </div>
        </div>

        <AuditRecordForm
          auditPlan={auditPlanQuery.data}
          existingRecord={recordQuery.data}
          isEdit={true}
          onSubmit={handleSubmit}
          submitting={updateMutation.isPending}
        />
      </Main>
    </>
  );
}
